import { UserStatus } from '@bp/bp-graphql-types';
import { Input, showToast } from '@bp/ui-components';
import { graphql } from 'client/gql';
import { Form, Formik } from 'formik';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'urql';
import { useAuthClaims } from '../../hooks/useAuthClaims';
import { ModalBottomButtons } from '../ModalBottomButtons/ModalBottomButtons';
import { schema } from './validation/schema';

const CreateTeachingTopicMutationDocument = graphql(`
  mutation BpCreateTeachingTopic($input: [TeachingTopicCreateInput!]!) {
    createTeachingTopics(input: $input) {
      info {
        nodesCreated
        __typename
      }
      teachingTopics {
        uuid
        __typename
        title
        topicParentConnection {
          __typename
          edges {
            __typename
          }
        }
      }
    }
  }
`);
const RenameTopicMutationDocument = graphql(`
  mutation BpRenameTopic($update: TeachingTopicUpdateInput!, $where: TeachingTopicWhere!) {
    updateTeachingTopics(update: $update, where: $where) {
      teachingTopics {
        uuid
        __typename
        title
        topicParentConnection {
          __typename
          edges {
            __typename
          }
        }
      }
    }
  }
`);

const TeachingTopicFormPageQueryDocument = graphql(`
  query TeachingTopicFormPageData($topicUuid: ID!, $organizationUuid: ID!) {
    teachingTopics(where: { uuid: $topicUuid, organization: { uuid: $organizationUuid } }) {
      uuid
      __typename
      title
    }
  }
`);

type TeachingTopicFormProps = {
  topicUuid?: string | null;
  parentTopicUuid?: string | null;
  courseUuid: string;
  onClose: () => void;
};

type TeachingTopicFormValues = { title: string };

export const TeachingTopicForm: FC<TeachingTopicFormProps> = ({ parentTopicUuid, topicUuid, courseUuid, onClose }) => {
  const edit = !!topicUuid;
  const { t } = useTranslation();
  const { pimAuthClaims } = useAuthClaims();
  const organizationUuid = pimAuthClaims.getOrganizationUuid();

  const [, createTeachingTopic] = useMutation(CreateTeachingTopicMutationDocument);
  const [, editTopic] = useMutation(RenameTopicMutationDocument);

  const [{ data }] = useQuery({
    query: TeachingTopicFormPageQueryDocument,
    variables: {
      topicUuid: topicUuid ?? '',
      organizationUuid,
    },
  });

  const initialValues: TeachingTopicFormValues = {
    title: edit ? (data?.teachingTopics[0]?.title ?? '') : '',
  };

  const handleSubmit = async (values: TeachingTopicFormValues) => {
    if (topicUuid && edit) {
      await editTopic({
        where: {
          uuid: topicUuid,
        },
        update: {
          title: values.title,
        },
      });
    } else {
      /*
       Create TeachingTopic
       */
      const owner = {
        connectOrCreate: {
          where: {
            node: {
              uuid: pimAuthClaims.getProfile().uuid ?? '',
            },
          },
          onCreate: {
            node: {
              uuid: pimAuthClaims.getProfile().uuid ?? '',
              managedBy: 'bp',
              userStatus: UserStatus.None,
              foreignRefId: [],
            },
          },
        },
      };
      const group = {
        connect: {
          where: {
            node: {
              uuid: courseUuid,
            },
          },
        },
      };
      const { error } = await createTeachingTopic({
        input: [
          {
            title: values?.title,
            owner: owner,
            organization: {
              connect: { where: { node: { uuid: organizationUuid } } },
            },
            group: group,
            topicParent: {
              connect: { where: { node: { uuid: parentTopicUuid } } },
            },
          },
        ],
      });
      if (error) {
        showToast(t('common.error'), {
          type: 'error',
        });
      } else {
        showToast(t('common.saved'), {
          type: 'success',
        });
      }
    }
    onClose();
  };

  return (
    <Formik<TeachingTopicFormValues>
      initialValues={initialValues}
      enableReinitialize={true}
      onSubmit={(values) => handleSubmit(values)}
      validationSchema={schema}
    >
      {({ handleBlur, handleChange, values, isSubmitting, errors }) => {
        return (
          <Form className={'bp__form'}>
            <Input
              autoFocus={true}
              label={t('common.title')}
              name={'title'}
              onBlur={handleBlur}
              onChange={handleChange}
              value={values.title?.toString()}
              error={errors.title?.toString()}
            />
            <ModalBottomButtons closeButton={{ callback: onClose }} isLoading={isSubmitting} errors={errors} />
          </Form>
        );
      }}
    </Formik>
  );
};
